<template>
  <div
    class="r-dropdown"
    :class="{ 'has-focus': hasFocus, 'has-value': hasValue, disable }"
    :style="{ '--input-bg-color': `var(--${inputBgColor})` }"
  >
    <RLabel v-if="!noLabel" v-bind="{ hasFocus, disable }">{{ label }}</RLabel>

    <q-select
      ref="select"
      v-model="model"
      :options="filteredOptions"
      :disable="disable"
      :multiple="multiple"
      :emit-value="emitValue"
      :map-options="mapOptions"
      :popup-content-class="popupContentClasses"
      :popup-content-style="{ '--align-menu': alignMenu, '--max-popup-height': maxPopupHeight + 'px' }"
      :virtual-scroll-sticky-size-start="showSearch ? 36 : 0"
      :virtual-scroll-item-size="32"
      :virtual-scroll-sticky-size-end="maxPopupHeight"
      :option-label="optionLabel"
      :option-value="optionValue"
      hide-dropdown-icon
      dense
      outlined
      @focus="hasFocus = true"
      @blur="hasFocus = false"
      @popup-show="onPopupShow"
      @popup-hide="onPopupHide"
      @filter="filterFn"
      @virtual-scroll="fixScrollPosition"
      v-on="$listeners"
    >
      <template v-if="!hasValue || multiple" #selected>
        <div v-if="!hasValue && withNA && options.length" class="text-body-1 text-ellipsis">
          <span :class="selectedItemColor">N/A</span>
        </div>

        <div v-else-if="!hasValue" class="text-caption-3 text-ellipsis">
          <span :class="[disable ? 'text-white-5' : 'text-grey2']">{{ placeholder }}</span>
        </div>

        <div v-else-if="multiple" class="text-body-1 text-ellipsis">
          <span :class="selectedItemColor">{{ model.length }} selected</span>
        </div>
      </template>

      <template v-else #selected-item="scope">
        <div class="text-body-1 text-ellipsis">
          <span :class="selectedItemColor">{{ isOptionWithObject ? scope.opt[optionLabel] : scope.opt }} </span>
        </div>
      </template>

      <template #append>
        <CaretIcon
          v-if="!loading"
          class="caret"
          :class="isPopupShown ? 'top' : 'bottom'"
          :disable="disable"
          :style="{ '--r-secondary-1': dark && !hasFocus ? 'white' : null }"
        />

        <IconWrapper v-if="loading">
          <q-spinner color="secondary-2" size="18px" />
        </IconWrapper>
      </template>

      <template v-if="multiple" #option="scope">
        <q-item v-bind="scope.itemProps" v-on="scope.itemEvents">
          <q-item-section side>
            <RCheckbox v-model="model" :val="isOptionWithObject ? scope.opt[optionValue] : scope.opt" />
          </q-item-section>

          <q-item-section>
            <q-item-label>{{ isOptionWithObject ? scope.opt[optionLabel] : scope.opt }}</q-item-label>
          </q-item-section>
        </q-item>
      </template>

      <template #no-option>
        <RInput
          v-show="showSearch"
          ref="inputSearch"
          v-model="search"
          is-search
          placeholder="Search"
          class="dropdown-search"
          no-label
          no-hint
          no-feedback-icon
          on-clear="focus"
        />

        <q-item>
          <q-item-section class="text-grey" style="grid-column: span 2">
            No results
          </q-item-section>
        </q-item>
      </template>

      <template v-for="(fn, scopedSlotName) in $scopedSlots" v-slot:[scopedSlotName]="props">
        <slot :props="props" :name="scopedSlotName" />
      </template>

      <template #before-options>
        <RInput
          v-show="showSearch"
          ref="inputSearch"
          v-model="search"
          is-search
          placeholder="Search"
          class="dropdown-search"
          no-label
          no-hint
          no-feedback-icon
          on-clear="focus"
        />

        <slot name="before-options"></slot>
      </template>

      <template v-if="withNA" #after-options>
        <q-item :active="model === null" class="dropdown-na" clickable @click="onNAOption">
          <q-item-section>
            <q-item-label>N/A</q-item-label>
          </q-item-section>
        </q-item>

        <slot name="after-options"></slot>
      </template>
    </q-select>

    <RHint v-if="!noHint" v-bind="{ feedback }">{{ hint }}</RHint>
  </div>
</template>

<script>
import { isObject, cloneDeep, orderBy } from 'lodash'

export default {
  components: {
    RLabel: () => import('@/ui/shared/RLabel'),
    RHint: () => import('@/ui/shared/RHint'),
    CaretIcon: () => import('@/ui/icons/CaretIcon'),
    IconWrapper: () => import('@/ui/icons/IconWrapper')
  },

  props: {
    feedback: {
      type: String,
      validator: val => !val || ['error', 'warning', 'success'].includes(val)
    },

    options: {
      type: Array,
      require: true
    },

    optionLabel: {
      type: String,
      default: 'label'
    },

    optionValue: {
      type: String,
      default: 'value'
    },

    alignMenu: {
      type: String,
      validator: val => ['start', 'end'].includes(val),
      default: 'start'
    },

    /**
     *  pass a boolean to manually control if the dropdown has value (something is selected)
     */
    customHasValue: {
      type: [String, Boolean],
      default: ''
    },

    /**
     *  Max height of the dropdown popup in px
     */
    maxPopupHeight: {
      type: Number,
      default: 200
    },

    popupClasses: {
      type: Array,
      default: () => []
    },

    /* Use CSS variable color without the prefix -- */
    inputBgColor: {
      type: String,
      default: 'r-white-1'
    },

    /**
     *  When you have an array of objects as options and you want to get the value
     *  as a model instead of the whole object you must pass those as `true`
     *  https://quasar.dev/vue-components/select#Affecting-model
     */
    emitValue: Boolean,
    mapOptions: Boolean,

    value: { required: true },
    multiple: Boolean,
    placeholder: String,
    label: String,
    hint: String,
    noLabel: Boolean,
    noHint: Boolean,
    loading: Boolean,
    disable: Boolean,
    noOptionsSort: Boolean,
    dark: Boolean,

    /* Add N/A option to the dropdown */
    withNA: Boolean
  },

  data: () => ({
    hasFocus: false,
    isPopupShown: false,
    search: null,
    filteredOptions: null,
    scrollPositionFixApplied: false
  }),

  computed: {
    model: {
      get() {
        return this.value
      },
      set(newVal) {
        this.$emit('input', newVal)
      }
    },

    isOptionWithObject() {
      return isObject(this.options[0])
    },

    hasValue() {
      /* `model` can be either array or a primitive type */
      if (this.customHasValue !== '') return this.customHasValue
      if (this.model === null) return false
      if (!Array.isArray(this.model)) return true
      return !!this.model.length
    },

    popupContentClasses() {
      const classes = ['dropdown__ui__cmp', ...this.popupClasses]
      if (this.multiple) classes.push('multiple')
      return classes.join(' ')
    },

    selectedItemColor() {
      if (this.disable) return 'text-white-5'
      if (this.hasFocus) return 'text-secondary-2'
      if (this.dark) return 'text-white-1'
      return 'text-secondary-1'
    },

    showSearch() {
      return this.options.length > 10
    }
  },

  watch: {
    search(newVal) {
      const filteringMode = newVal !== null && !!newVal.trim()

      this.$emit('filtering-mode', filteringMode)
      this.showSearch && this.$refs.select.filter(newVal)
    },

    options: {
      immediate: true,
      deep: true,
      handler(newVal) {
        if (newVal) this.setFilteredOptions(newVal)
        else this.filteredOptions = []
      }
    }
  },

  methods: {
    filterFn(val, update) {
      if (val === null || !val.trim()) {
        update(() => this.setFilteredOptions(this.options))
        return
      }

      update(() => {
        const needle = val.toLowerCase()
        const filteredOptions = this.options.filter(v => v[this.optionLabel].toLowerCase().indexOf(needle) > -1)
        this.setFilteredOptions(filteredOptions)
      })
    },

    onPopupShow() {
      this.isPopupShown = true

      this.$nextTick(() => {
        if (this.showSearch) {
          this.$refs.select.filter(this.search)
          this.$refs.inputSearch.focus()
          this.$refs.select.updateMenuPosition()
        }
      })
    },

    fixScrollPosition() {
      if (this.multiple && !this.scrollPositionFixApplied) {
        const menu = document.querySelector('.dropdown__ui__cmp')
        menu.scrollTo(0, 0)
        this.scrollPositionFixApplied = true
      }
    },

    onPopupHide() {
      this.isPopupShown = false
      this.scrollPositionFixApplied = false
    },

    setFilteredOptions(options) {
      if (this.noOptionsSort) {
        this.filteredOptions = cloneDeep(options)
        return
      }

      if (this.isOptionWithObject) {
        const clonedOptions = cloneDeep(options)
        this.filteredOptions = orderBy(clonedOptions, [o => o[this.optionLabel]])
      } else {
        this.filteredOptions = orderBy([...options])
      }
    },

    onNAOption() {
      this.$refs.select.toggleOption(null)
    }
  }
}
</script>

<!--Scoped styles do not work-->
<style lang="scss">
@import '~@/styles/shadows';
@import '~@/styles/fonts';
@import '~@/styles/mixins';

.dropdown__ui__cmp {
  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background-color: var(--r-secondary-1);
  }
}

.dropdown__ui__cmp {
  @extend .shadow2;
  max-height: var(--max-popup-height);
  width: 250px;

  .dropdown-search {
    z-index: 1;
    position: sticky;
    top: 0;
    background: var(--r-white-1);

    .input-component {
      border-bottom: 1px solid var(--r-white-2);

      .q-field__control {
        border: none !important;
      }
    }
  }

  .dropdown-na {
    position: sticky;
    bottom: 0;
    background: var(--r-white-1);
    border-top: 1px solid var(--r-white-4);
  }

  .q-item {
    min-height: var(--r-space-xl);
    height: var(--r-space-xl);
    color: var(--r-secondary-2);
    padding: 0 var(--r-space-sm);
    @extend .text-body-1;
    display: grid;

    .q-focus-helper {
      display: none;
    }

    &:hover {
      background: var(--r-white-2);
    }
  }

  &.multiple {
    .q-item {
      grid-template-columns: 18px 1fr;
      gap: var(--r-space-sm);
    }
  }

  &:not(.multiple) {
    .q-item {
      justify-items: var(--align-menu);

      &--active {
        background: var(--r-primary-1);
        @extend .text-body-4;
        color: var(--r-white-1);
        text-transform: uppercase;
      }
    }
  }
}
</style>

<style lang="scss" scoped>
@import '~@/styles/fonts';
@import '~@/styles/mixins';
@import '~@/styles/general';

.r-dropdown {
  .caret {
    transition: transform 0.3s ease-in-out;

    &.bottom {
      transform: rotate(0);
    }

    &.top {
      transform: rotate(180deg);
    }
  }

  @include q-field__control {
    height: var(--r-field-height);
    min-height: var(--r-field-height) !important;
    background: var(--input-bg-color);
    border: 1px solid var(--r-white-5);
    padding: 0 0 0 var(--r-space-md) !important;

    .q-field__native {
      height: var(--r-field-height);
      min-height: var(--r-field-height) !important;
    }

    &:before,
    &:after {
      display: none !important;
    }

    .q-field__append {
      height: var(--r-field-height);
      padding: 0 !important;
    }

    &:hover {
      border: 1px solid var(--r-secondary-4);
    }
  }

  &.has-focus {
    @include q-field__control {
      background: var(--r-white-1);
      border: 1px solid var(--r-secondary-4);
    }
  }

  &.disable {
    @include q-field__control {
      background: rgba(var(--r-white-1-rgb), 0.65);
      border: 1px solid var(--r-white-3);

      > div {
        opacity: 1 !important;
      }
    }
  }
}
</style>

<docs>
### Dropdown

```jsx
<template>
  <Grid gaf="column" justify-content="center" align-items="center" align-content="center" gap="15px">
    <div class="box" style="width: 200px; align-self: start; background: rgba(125, 246, 246, 0.1)">
      <RDropdown
        v-model="value"
        :options="options"
        map-options
        emit-value
        v-bind="{ disable, loading, noHint, noLabel, placeholder, hint, label, feedback, multiple, alignMenu, noOptionsSort: !sortOptions, withNA }"
      />
    </div>

    <Grid justify-content="center" align-items="center" gap="10px">
      <Grid gtc="100px 1fr" gap="10px" align-items="center">
        <span>Feedback</span>
        <q-select v-model="feedback" dense outlined :options="feedbackOptions" emit-value map-options></q-select>
      </Grid>

      <Grid gtc="100px 1fr" gap="10px" align-items="center">
        <span>Align menu</span>
        <q-select v-model="alignMenu" dense outlined :options="alignMenuOptions"></q-select>
      </Grid>

      <Grid gtc="100px 1fr" gap="10px" align-items="center">
        <span>Placeholder</span>
        <q-input v-model="placeholder" dense outlined></q-input>
      </Grid>

      <Grid gtc="100px 1fr" gap="10px" align-items="center">
        <span>Label</span>
        <q-input v-model="label" dense outlined></q-input>
      </Grid>

      <Grid gtc="100px 1fr" gap="10px" align-items="center">
        <span>Hint</span>
        <q-input v-model="hint" dense outlined></q-input>
      </Grid>

      <Grid gtc="100px 1fr" gap="10px" align-items="center">
        <span>Remove label</span>
        <q-checkbox v-model="noLabel" dense />
      </Grid>

      <Grid gtc="100px 1fr" gap="10px" align-items="center">
        <span>Remove hint</span>
        <q-checkbox v-model="noHint" dense />
      </Grid>

      <Grid gtc="100px 1fr" gap="10px" align-items="center">
        <span>Loading</span>
        <q-checkbox v-model="loading" dense />
      </Grid>

      <Grid gtc="100px 1fr" gap="10px" align-items="center">
        <span>Disable</span>
        <q-checkbox v-model="disable" dense />
      </Grid>

      <Grid gtc="100px 1fr" gap="10px" align-items="center">
        <span>Multiple</span>
        <q-checkbox :value="multiple" dense @input="toggleMultiple" />
      </Grid>

      <Grid gtc="100px 1fr" gap="10px" align-items="center">
        <span>Sort options</span>
        <q-checkbox v-model="sortOptions" dense />
      </Grid>

      <Grid gtc="100px 1fr" gap="10px" align-items="center">
        <span>Add N/A option</span>
        <q-checkbox v-model="withNA" dense />
      </Grid>

      <div class="options">
        <div class="text-heading-3">Add options to the dropdown</div>

        <Grid gtc="1fr 1fr auto" align-items="center" gap="var(--r-space-md)">
          <q-input label="Label" v-model="optionsLabel" dense outlined></q-input>
          <q-input readonly label="Value" :value="optionsValue" dense outlined></q-input>

          <q-btn :disable="!optionsLabel" icon="add" round @click="addOption"></q-btn>
        </Grid>

        <hr>

        <div class="text-caption-2">* If you add more than 10 options a search will appear inside the popup</div>
        <div class="text-heading-3">Options:</div>

        <div v-for="option of options" :key="option.value">
          <code>{ label: {{ option.label }}, value: {{ option.value }} } <q-btn icon="delete" dense size="xs" rounded @click="deleteOption(option.value)"></q-btn> </code>
        </div>

        <div v-if="!options.length" class="text-caption-3">No options</div>

        <div class="q-pa-md">
          <q-btn v-if="options.length > 3" @click="options = []">Remove all options</q-btn>
        </div>
      </div>
    </Grid>
  </Grid>
</template>

<script>
  export default {
    data: () => ({
      value: null,
      options: [
        { label: 'Option 1', value: 'option-1' },
        { label: 'Option 2', value: 'option-2' },
        { label: 'Option 47', value: 'option-47' },
        { label: 'Option 983', value: 'option-983' },
        { label: 'Option 947', value: 'option-947' },
        { label: 'Option 212', value: 'option-212' },
        { label: 'Option 310', value: 'option-310' },
        { label: 'Option 326', value: 'option-326' },
        { label: 'Option 254', value: 'option-254' },
        { label: 'Option 255', value: 'option-255' },
        { label: 'Option 256', value: 'option-256' },
      ],
      label: '',
      placeholder: '',
      hint: '',
      feedback: '',
      feedbackOptions: [
        { label: 'None', value: '' },
        { label: 'Error', value: 'error' },
        { label: 'Warning', value: 'warning' },
        { label: 'Success', value: 'success' }
      ],
      noHint: false,
      noLabel: false,
      loading: false,
      disable: false,
      multiple: false,
      alignMenu: 'start',
      alignMenuOptions: ['start', 'end'],
      optionsLabel: null,
      sortOptions: true,
      withNA: false
    }),

    computed: {
      optionsValue () {
        if (!this.optionsLabel) return null

        return this.optionsLabel.toLowerCase().replace(" ", "-")
      }
    },

    methods: {
      toggleMultiple(state) {
        if (state) {
          this.value = []
          this.multiple = true
        } else {
          this.value = null
          this.multiple = false
        }
      },

      addOption () {
        this.options.push({ label: this.optionsLabel, value: this.optionsValue })
        this.optionsLabel = null
      },

      deleteOption (optionValue) {
        this.options = [...this.options.filter(o => o.value !== optionValue)]
      }
    }
  }
</script>
```
</docs>
